import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import type {
  CampaignStats,
  Disposition,
  DispositionsResponse,
  PowerDialerCampaign,
  PowerDialerProspect,
  ProspectDetail,
  VmDrop,
} from '@/types/powerDialer'

// =====================================================
// MANAGER : statut, numéros, activation
// =====================================================

export interface PowerDialerStatus {
  power_dialer_enabled: boolean
}

export function usePowerDialerStatus() {
  return useQuery({
    queryKey: ['power-dialer-status'],
    queryFn: async () => {
      const { data } = await api.get<PowerDialerStatus>(
        '/api/power-dialer/status/',
      )
      return data
    },
  })
}

export interface AvailablePDNumber {
  phoneNumber: string
  friendlyName: string
  locality: string
  region: string
}

interface AvailablePDNumbersResponse {
  ok?: boolean
  data: AvailablePDNumber[]
}

export type PDNumberSearchType = 'local' | 'tollfree'

/** Pays d'achat des numéros Power Dialer (Twilio available_phone_numbers). */
export type PDNumberCountry = 'CA' | 'US'

interface UseAvailablePDNumbersOptions {
  areaCode?: string
  type?: PDNumberSearchType
  country?: PDNumberCountry
  enabled?: boolean
}

/**
 * Recherche numéros Twilio achetables. 2 modes :
 *  - `type='local'` (défaut) : numéros locaux par area code (514, 438, 418...).
 *    Indispensable pour la prospection — local presence = +30-50% contact rate.
 *  - `type='tollfree'` : numéros sans frais (800, 833, 844, 855, 866, 877, 888).
 *    Pour les campagnes corporate / image marque. Pas d'area code requis.
 */
export function useAvailablePDNumbers({
  areaCode = '',
  type = 'local',
  country = 'CA',
  enabled = true,
}: UseAvailablePDNumbersOptions = {}) {
  return useQuery({
    queryKey: ['power-dialer-available-numbers', country, type, areaCode],
    queryFn: async () => {
      const params = new URLSearchParams({ country, type })
      if (type === 'local' && areaCode.trim()) {
        params.set('area_code', areaCode.trim())
      }
      const { data } = await api.get<AvailablePDNumbersResponse>(
        `/api/power-dialer/numbers/available/?${params.toString()}`,
      )
      return data.data ?? []
    },
    enabled,
    staleTime: 30_000,
  })
}

export function useActivatePowerDialer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (numbers: string[]) => {
      const { data } = await api.post<{ ok: boolean }>(
        '/api/power-dialer/activate/',
        { numbers },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-status'] })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-owned-numbers'],
      })
    },
  })
}

export interface OwnedPDNumber {
  id: number
  phone_number: string
  label: string
}

export function useOwnedPDNumbers(enabled = true) {
  return useQuery({
    queryKey: ['power-dialer-owned-numbers'],
    queryFn: async () => {
      const { data } = await api.get<
        { numbers?: OwnedPDNumber[] } | OwnedPDNumber[]
      >('/api/power-dialer/numbers/owned/')
      if (Array.isArray(data)) return data
      return data.numbers ?? []
    },
    enabled,
  })
}

interface PurchasePDNumberResponse {
  ok: boolean
  id: number
  phone_number: string
  number_sid: string
}

/**
 * POST /api/power-dialer/numbers/purchase/
 * Achète un numéro Twilio dans le subaccount Power Dialer.
 * Body: { phone_number: '+14161234567' } (format E.164 obligatoire).
 */
export function usePurchasePDNumber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      const { data } = await api.post<PurchasePDNumberResponse>(
        '/api/power-dialer/numbers/purchase/',
        { phone_number: phoneNumber },
      )
      return data
    },
    onSuccess: () => {
      // Refresh la liste des numéros owned + le status (count peut être affiché)
      queryClient.invalidateQueries({ queryKey: ['power-dialer-owned-numbers'] })
      queryClient.invalidateQueries({ queryKey: ['power-dialer-status'] })
    },
  })
}

// =====================================================
// MANAGER : campagnes (liste/create/delete/CSV import)
// =====================================================

export interface PowerDialerCampaignSummary {
  id: number
  name: string
  description: string
  status: string
  total_prospects: number
  prospects_called: number
  prospects_interested: number
  agents_count: number
  numbers_count: number
  created_at: string
  local_presence_enabled?: boolean
}

export function usePowerDialerCampaigns() {
  return useQuery({
    queryKey: ['power-dialer-campaigns'],
    queryFn: async () => {
      const { data } = await api.get<
        { campaigns?: PowerDialerCampaignSummary[] } | PowerDialerCampaignSummary[]
      >('/api/power-dialer/campaigns/')
      if (Array.isArray(data)) return data
      return data.campaigns ?? []
    },
  })
}

export interface CreateCampaignInput {
  name: string
  description?: string
  script?: string
  number_ids?: number[]
  agent_ids?: number[]
  max_attempts?: number
  daily_cap_per_number?: number
  auto_rotation?: boolean
  local_presence_enabled?: boolean
  status?: string
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateCampaignInput) => {
      const { data } = await api.post<{ id: number }>(
        '/api/power-dialer/campaigns/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-campaigns'] })
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (campaignId: number) => {
      await api.delete(`/api/power-dialer/campaigns/${campaignId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-campaigns'] })
    },
  })
}

export interface ImportProspectsResult {
  imported: number
  skipped: number
}

export function useImportProspects(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post<ImportProspectsResult>(
        `/api/power-dialer/campaigns/${campaignId}/import-csv/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-campaigns'] })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospects', campaignId],
      })
    },
  })
}

// =====================================================
// MES CAMPAGNES (vue agent — liste)
// =====================================================

export interface PowerDialerMyCampaign {
  id: number
  name: string
  description: string
  status: string
  total_prospects: number
  prospects_called: number
  prospects_interested: number
}

export function usePowerDialerMyCampaigns() {
  return useQuery({
    queryKey: ['power-dialer-my-campaigns'],
    queryFn: async () => {
      const { data } = await api.get<
        { campaigns?: PowerDialerMyCampaign[] } | PowerDialerMyCampaign[]
      >('/api/power-dialer/my-campaigns/')
      if (Array.isArray(data)) return data
      return data.campaigns || []
    },
  })
}

// =====================================================
// DÉTAIL CAMPAGNE
// =====================================================

export function useCampaignDetail(campaignId: number | null) {
  return useQuery({
    queryKey: ['power-dialer-campaign', campaignId],
    queryFn: async () => {
      const { data } = await api.get<PowerDialerCampaign>(
        `/api/power-dialer/campaigns/${campaignId}/`,
      )
      return data
    },
    enabled: !!campaignId,
  })
}

export function useUpdateCampaign(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<PowerDialerCampaign>) => {
      const { data } = await api.patch<PowerDialerCampaign>(
        `/api/power-dialer/campaigns/${campaignId}/`,
        patch,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-campaign', campaignId],
      })
      queryClient.invalidateQueries({ queryKey: ['power-dialer-my-campaigns'] })
    },
  })
}

// =====================================================
// PROSPECTS DE LA CAMPAGNE
// =====================================================

export function useCampaignProspects(campaignId: number | null) {
  return useQuery({
    queryKey: ['power-dialer-prospects', campaignId],
    queryFn: async () => {
      const { data } = await api.get<
        { prospects?: PowerDialerProspect[] } | PowerDialerProspect[]
      >(`/api/power-dialer/campaigns/${campaignId}/prospects/`)
      if (Array.isArray(data)) return data
      return data.prospects || []
    },
    enabled: !!campaignId,
  })
}

export interface CreateProspectInput {
  phone: string
  first_name?: string
  last_name?: string
}

export function useCreateProspect(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateProspectInput) => {
      const { data } = await api.post<PowerDialerProspect>(
        `/api/power-dialer/campaigns/${campaignId}/prospects/`,
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospects', campaignId],
      })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-campaign-stats', campaignId],
      })
    },
  })
}

// =====================================================
// STATS CAMPAGNE
// =====================================================

export function useCampaignStats(campaignId: number | null) {
  return useQuery({
    queryKey: ['power-dialer-campaign-stats', campaignId],
    queryFn: async () => {
      const { data } = await api.get<CampaignStats>(
        `/api/power-dialer/campaigns/${campaignId}/stats/`,
      )
      return data
    },
    enabled: !!campaignId,
  })
}

// =====================================================
// DÉTAIL PROSPECT (call_logs + notes)
// =====================================================

export function useProspectDetail(
  campaignId: number | null,
  prospectId: number | null,
) {
  return useQuery({
    queryKey: ['power-dialer-prospect', campaignId, prospectId],
    queryFn: async () => {
      const { data } = await api.get<ProspectDetail>(
        `/api/power-dialer/campaigns/${campaignId}/prospects/${prospectId}/`,
      )
      return data
    },
    enabled: !!campaignId && !!prospectId,
  })
}

export interface UpdateProspectInput {
  // Slug libre depuis Disposition.slug (les 6 par défaut OU custom).
  status?: string
  // ISO 8601 — requis quand on choisit une disposition de catégorie 'callback'.
  callback_date?: string | null
}

export function useUpdateProspect(campaignId: number, prospectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: UpdateProspectInput) => {
      const { data } = await api.patch<ProspectDetail>(
        `/api/power-dialer/campaigns/${campaignId}/prospects/${prospectId}/`,
        patch,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospects', campaignId],
      })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospect', campaignId, prospectId],
      })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-campaign-stats', campaignId],
      })
    },
  })
}

export function useLogProspectCall(campaignId: number, prospectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (call_sid: string) => {
      const { data } = await api.post<{ attempts: number }>(
        `/api/power-dialer/campaigns/${campaignId}/prospects/${prospectId}/log-call/`,
        { call_sid },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospects', campaignId],
      })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospect', campaignId, prospectId],
      })
    },
  })
}

export function useCreateProspectNote(campaignId: number, prospectId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (content: string) => {
      const { data } = await api.post(
        `/api/power-dialer/campaigns/${campaignId}/prospects/${prospectId}/notes/`,
        { content },
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-prospect', campaignId, prospectId],
      })
    },
  })
}

// =====================================================
// VM DROPS (voicemail drops)
// =====================================================

export function useVmDrops() {
  return useQuery({
    queryKey: ['power-dialer-vm-drops'],
    queryFn: async () => {
      // Le backend renvoie `{voicemail_drops: [...]}` (cf. views_power_dialer.py).
      // On accepte aussi un array brut ou la clé `drops` pour la robustesse.
      const { data } = await api.get<
        { voicemail_drops?: VmDrop[]; drops?: VmDrop[] } | VmDrop[]
      >('/api/power-dialer/voicemail-drops/')
      if (Array.isArray(data)) return data
      return data.voicemail_drops || data.drops || []
    },
  })
}

export interface UploadVmDropInput {
  audio: Blob
  name: string
  duration: number
}

export function useUploadVmDrop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ audio, name, duration }: UploadVmDropInput) => {
      const token = useAuthStore.getState().accessToken
      const formData = new FormData()
      formData.append('audio', audio, `${name}.webm`)
      formData.append('name', name)
      formData.append('duration', String(duration))
      const res = await fetch(`${API_BASE_URL}/api/power-dialer/voicemail-drops/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `upload failed: ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-vm-drops'] })
    },
  })
}

export function useDeleteVmDrop() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/power-dialer/voicemail-drops/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-vm-drops'] })
    },
  })
}

export function useExecuteVmDrop() {
  return useMutation({
    mutationFn: async ({
      call_sid,
      drop_id,
    }: {
      call_sid: string
      drop_id: number
    }) => {
      const { data } = await api.post('/api/power-dialer/voicemail-drops/execute/', {
        call_sid,
        drop_id,
      })
      return data
    },
  })
}

// =====================================================
// DISPOSITIONS (personnalisables)
// =====================================================

export function useCompanyDispositions() {
  return useQuery({
    queryKey: ['power-dialer-dispositions', 'company'],
    queryFn: async () => {
      const { data } = await api.get<{ dispositions: Disposition[] }>(
        '/api/power-dialer/dispositions/',
      )
      return data.dispositions
    },
  })
}

export function useCampaignDispositions(campaignId: number | null) {
  return useQuery({
    queryKey: ['power-dialer-dispositions', 'campaign', campaignId],
    queryFn: async () => {
      const { data } = await api.get<DispositionsResponse>(
        `/api/power-dialer/campaigns/${campaignId}/dispositions/`,
      )
      return data
    },
    enabled: !!campaignId,
  })
}

export interface DispositionInput {
  slug: string
  name: string
  category: Disposition['category']
  color?: string
  icon?: string
  is_final?: boolean
  order?: number
  campaign?: number | null
}

export function useCreateDisposition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: DispositionInput) => {
      const { data } = await api.post<Disposition>(
        '/api/power-dialer/dispositions/',
        input,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-dispositions'] })
    },
  })
}

export function useUpdateDisposition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number
      patch: Partial<DispositionInput>
    }) => {
      const { data } = await api.patch<Disposition>(
        `/api/power-dialer/dispositions/${id}/`,
        patch,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-dispositions'] })
    },
  })
}

export function useDeleteDisposition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/power-dialer/dispositions/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-dispositions'] })
    },
  })
}

export function useOverrideCampaignDispositions(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<DispositionsResponse>(
        `/api/power-dialer/campaigns/${campaignId}/dispositions/override/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-dispositions', 'campaign', campaignId],
      })
    },
  })
}

export function useResetCampaignDispositions(campaignId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<DispositionsResponse>(
        `/api/power-dialer/campaigns/${campaignId}/dispositions/reset/`,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-dispositions', 'campaign', campaignId],
      })
    },
  })
}

// ============================================================
// Caller ID intelligent (Local Presence + Sticky + Rotation)
// ============================================================

export interface ResolvedCallerId {
  caller_id: string
  // manual | pinned | sticky | local_presence | rotation | agent_fallback | campaign_default
  source: string
  pinned_blocked: boolean
}

/**
 * Demande au backend LE caller ID à afficher pour cet appel (source de
 * vérité : sticky, cap quotidien et rotation vivent en DB). À appeler au
 * moment de composer — le backend mémorise le sticky et incrémente le cap.
 */
export async function resolveCallerId(
  campaignId: number,
  prospectPhone: string,
  agentSelected?: string,
  manualOverride?: string,
): Promise<ResolvedCallerId> {
  const { data } = await api.post<ResolvedCallerId>(
    `/api/power-dialer/campaigns/${campaignId}/resolve-caller-id/`,
    {
      prospect_phone: prospectPhone,
      agent_selected: agentSelected || undefined,
      // Mode Manuel : choix explicite de l'agent (niveau 1, borné au pool).
      manual_override: manualOverride || undefined,
    },
  )
  return data
}

export interface CallerIdPinStatus {
  caller_id: string | null
  pinned: boolean
}

/**
 * État du numéro mémorisé pour un contact DANS UNE CAMPAGNE (sticky auto ou
 * figé). La mémoire est scopée par campagne côté serveur.
 */
export function useCallerIdPinStatus(campaignId?: number, contactPhone?: string) {
  return useQuery({
    queryKey: ['caller-id-pin', campaignId, contactPhone],
    enabled: !!contactPhone && !!campaignId,
    queryFn: async () => {
      const { data } = await api.get<CallerIdPinStatus>(
        '/api/power-dialer/caller-id/pin/',
        { params: { campaign_id: campaignId, contact_phone: contactPhone } },
      )
      return data
    },
  })
}

/** Fige un numéro pour un contact dans cette campagne (priorité sur sticky). */
export function usePinCallerId(campaignId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { contact_phone: string; caller_id: string }) => {
      const { data } = await api.post('/api/power-dialer/caller-id/pin/', {
        campaign_id: campaignId,
        ...payload,
      })
      return data
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['caller-id-pin', campaignId, vars.contact_phone] }),
  })
}

/** Libère le pin d'un contact dans cette campagne (repasse en sticky auto). */
export function useUnpinCallerId(campaignId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactPhone: string) => {
      const { data } = await api.delete('/api/power-dialer/caller-id/pin/', {
        data: { campaign_id: campaignId, contact_phone: contactPhone },
      })
      return data
    },
    onSuccess: (_d, contactPhone) =>
      qc.invalidateQueries({ queryKey: ['caller-id-pin', campaignId, contactPhone] }),
  })
}
